import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, CheckIcon, XIcon } from 'lucide-react';
interface SelectOption {
  value: string;
  label: string;
}
interface SelectProps {
  label?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  className?: string;
}
export function Select({
  label,
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  error,
  disabled = false,
  searchable = true,
  clearable = true,
  className = ''
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedOption = options.find(opt => opt.value === value);
  const filteredOptions = searchTerm ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);
  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      handleSelect(filteredOptions[0].value);
    }
  };
  return <div className={`w-full ${className}`} ref={containerRef} data-id="element-2360">
      {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2361">
          {label}
        </label>}
      <div className="relative" data-id="element-2362">
        <div onClick={() => !disabled && setIsOpen(!isOpen)} className={`
            w-full px-3 py-2 rounded-lg border cursor-pointer
            bg-white dark:bg-gray-800
            text-gray-900 dark:text-gray-100
            transition-colors duration-150
            flex items-center justify-between
            ${disabled ? 'bg-gray-100 cursor-not-allowed dark:bg-gray-700' : ''}
            ${error ? 'border-red-500 focus:ring-red-500' : isOpen ? 'border-sky-500 ring-2 ring-sky-500' : 'border-gray-300 dark:border-gray-600'}
          `} data-id="element-2363">
          <span className={selectedOption ? '' : 'text-gray-400 dark:text-gray-500'} data-id="element-2364">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <div className="flex items-center gap-1" data-id="element-2365">
            {clearable && value && !disabled && <button onClick={handleClear} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" type="button" data-id="element-2366">
                <XIcon className="w-4 h-4 text-gray-400" data-id="element-2367" />
              </button>}
            <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-2368" />
          </div>
        </div>

        {isOpen && !disabled && <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-hidden" data-id="element-2369">
            {searchable && <div className="p-2 border-b border-gray-200 dark:border-gray-700" data-id="element-2370">
                <input ref={inputRef} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={handleKeyDown} placeholder="Buscar..." className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-2371" />
              </div>}
            <div className="overflow-y-auto max-h-48" data-id="element-2372">
              {filteredOptions.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-2373">
                  No se encontraron resultados
                </div> : filteredOptions.map(option => <div key={option.value} onClick={() => handleSelect(option.value)} className={`
                      px-3 py-2 cursor-pointer flex items-center justify-between
                      hover:bg-sky-50 dark:hover:bg-sky-900/20
                      ${option.value === value ? 'bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400' : 'text-gray-900 dark:text-gray-100'}
                    `} data-id="element-2374">
                    <span data-id="element-2375">{option.label}</span>
                    {option.value === value && <CheckIcon className="w-4 h-4 text-sky-500" data-id="element-2376" />}
                  </div>)}
            </div>
          </div>}
      </div>
      {error && <p className="mt-1 text-sm text-red-500" data-id="element-2377">{error}</p>}
    </div>;
}